(() => {
  const MOBILE_QUERY = '(max-width: 749px)';
  const MOTION_DURATION = 280;
  const ROOT_SELECTOR = '[data-ulif-mobile-drawer]';
  const TRIGGER_SELECTOR = '[data-ulif-menu-trigger]';
  const PANEL_SELECTOR = '[data-ulif-menu-panel]';
  const ACCORDION_SELECTOR = '[data-ulif-accordion]';
  const ACCORDION_TRIGGER_SELECTOR = '[data-ulif-accordion-trigger]';
  const ACCORDION_PANEL_SELECTOR = '[data-ulif-accordion-panel]';

  class UlifMobileDrawer extends HTMLElement {
    constructor() {
      super();
      this.state = {
        drawerOpen: false,
        openSection: null,
        openGroup: null,
      };
      this.abortController = new AbortController();
      this.closeTimer = null;
      this.scrollY = null;
      this.searchStateObserver = null;
      this.trigger = null;
      this.panel = null;
    }

    connectedCallback() {
      if (this.dataset.controllerReady === 'true') return;
      if (this.abortController.signal.aborted) this.abortController = new AbortController();

      this.trigger = this.querySelector(`:scope > ${TRIGGER_SELECTOR}`);
      this.panel = this.querySelector(`:scope > ${PANEL_SELECTOR}`);
      if (!this.trigger || !this.panel) return;

      const { signal } = this.abortController;
      this.addEventListener('click', this.handleClick, { signal });
      document.addEventListener('keydown', this.handleDocumentKeydown, { capture: true, signal });
      document.addEventListener('focusin', this.handleDocumentFocusIn, { capture: true, signal });
      document.addEventListener('ulif:search-open', this.handleSearchOpen, { signal });
      window.addEventListener('resize', this.handleResize, { signal });

      this.dataset.controllerReady = 'true';
      this.hydrateState();
      this.observeSearchState();
    }

    disconnectedCallback() {
      this.abortController.abort();
      window.clearTimeout(this.closeTimer);
      window.clearTimeout(this.__ulifDrawerCloseTimer);
      this.searchStateObserver?.disconnect();
      this.searchStateObserver = null;
      this.querySelectorAll(ACCORDION_SELECTOR).forEach((accordion) => {
        window.clearTimeout(accordion.__ulifCloseTimer);
      });

      if (this.state.drawerOpen || this.dataset.state === 'open' || this.dataset.state === 'closing') {
        this.forceClosedState();
      }

      delete this.dataset.controllerReady;
    }

    get isMobile() {
      return window.matchMedia(MOBILE_QUERY).matches;
    }

    get reduceMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    hydrateState() {
      if (!this.isMobile) {
        this.forceClosedState();
        return;
      }

      if (this.dataset.state === 'closing') {
        window.clearTimeout(this.__ulifDrawerCloseTimer);
        this.state.drawerOpen = false;
        this.classList.remove('menu-open');
        this.trigger?.setAttribute('aria-expanded', 'false');
        this.updateTriggerLabel(false);
        this.updateDialogState(false);
        this.panel?.setAttribute('aria-hidden', 'true');
        if (this.panel) this.panel.inert = true;
        this.syncAccordionState();

        if (this.reduceMotion) this.finishClose(false);
        else this.closeTimer = window.setTimeout(() => this.finishClose(false), MOTION_DURATION);
        return;
      }

      const openedByBootstrap =
        this.dataset.state === 'open' || this.trigger?.getAttribute('aria-expanded') === 'true';

      this.state.drawerOpen = openedByBootstrap;
      this.syncAccordionState();

      if (openedByBootstrap) {
        this.dataset.state = 'open';
        this.classList.add('menu-open');
        this.updateDialogState(true);
        this.trigger?.setAttribute('aria-expanded', 'true');
        this.updateTriggerLabel(true);
        this.panel?.setAttribute('aria-hidden', 'false');
        if (this.panel) this.panel.inert = false;
        this.lockPage();
      } else {
        this.dataset.state = 'closed';
        this.classList.remove('menu-open');
        this.updateDialogState(false);
        this.trigger?.setAttribute('aria-expanded', 'false');
        this.updateTriggerLabel(false);
        this.panel?.setAttribute('aria-hidden', 'true');
        if (this.panel) this.panel.inert = true;
      }
    }

    syncAccordionState() {
      this.state.openSection = null;
      this.state.openGroup = null;

      this.querySelectorAll(`${ACCORDION_SELECTOR}.is-expanded`).forEach((accordion) => {
        const key = accordion.dataset.key || null;
        if (accordion.dataset.level === 'section') this.state.openSection = key;
        if (accordion.dataset.level === 'group') this.state.openGroup = key;
      });
    }

    handleClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const menuTrigger = target.closest(TRIGGER_SELECTOR);
      if (menuTrigger && this.contains(menuTrigger)) {
        event.preventDefault();
        this.toggle();
        return;
      }

      const accordionTrigger = target.closest(ACCORDION_TRIGGER_SELECTOR);
      if (accordionTrigger && this.contains(accordionTrigger)) {
        event.preventDefault();
        this.toggleAccordion(accordionTrigger);
        return;
      }

      const link = target.closest('a[href]');
      if (link && this.panel?.contains(link)) {
        this.close({ immediate: true, restoreFocus: false });
      }
    };

    handleDocumentKeydown = (event) => {
      if (!this.state.drawerOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.close({ restoreFocus: true });
        return;
      }

      if (event.key === 'Tab') this.trapTabKey(event);
    };

    handleDocumentFocusIn = (event) => {
      if (!this.state.drawerOpen || !(event.target instanceof Element)) return;

      const search = event.target.closest('ulif-header-search');
      if (search && !this.contains(search)) {
        this.close({ restoreFocus: false });
        return;
      }

      if (!this.contains(event.target)) {
        event.stopPropagation();
        this.trigger?.focus({ preventScroll: true });
      }
    };

    handleSearchOpen = () => {
      if (
        this.state.drawerOpen &&
        document.documentElement.classList.contains('ulif-search-is-open')
      ) {
        this.close({ restoreFocus: false });
      }
    };

    handleResize = () => {
      if (!this.isMobile && (this.state.drawerOpen || this.dataset.state === 'closing')) {
        this.close({ immediate: true, restoreFocus: false });
      }
    };

    toggle() {
      if (this.state.drawerOpen) this.close({ restoreFocus: true });
      else this.open();
    }

    open() {
      if (!this.isMobile || this.state.drawerOpen || !this.trigger || !this.panel) return;

      for (const drawer of document.querySelectorAll(`${ROOT_SELECTOR}[data-state='open']`)) {
        if (drawer !== this && typeof drawer.close === 'function') {
          drawer.close({ immediate: true, restoreFocus: false });
        }
      }

      for (const search of document.querySelectorAll('ulif-header-search[data-open]')) {
        if (typeof search.closeSearch === 'function') search.closeSearch(false);
      }

      window.clearTimeout(this.closeTimer);
      this.state.drawerOpen = true;
      this.dataset.state = 'open';
      this.classList.add('menu-open');
      this.updateDialogState(true);
      this.trigger.setAttribute('aria-expanded', 'true');
      this.updateTriggerLabel(true);
      this.panel.setAttribute('aria-hidden', 'false');
      this.panel.inert = false;
      this.lockPage();

      this.dispatchEvent(
        new CustomEvent('ulif:mobile-drawer-open', { bubbles: true, detail: { drawer: this } })
      );
    }

    close({ immediate = false, restoreFocus = true } = {}) {
      if (!this.trigger || !this.panel) return;
      if (this.dataset.state === 'closing') {
        if (immediate) this.finishClose(restoreFocus);
        return;
      }
      if (!this.state.drawerOpen && this.dataset.state !== 'open') return;

      window.clearTimeout(this.closeTimer);
      this.state.drawerOpen = false;
      this.dataset.state = 'closing';
      this.classList.remove('menu-open');

      if (restoreFocus && this.panel.contains(document.activeElement)) {
        this.trigger.focus({ preventScroll: true });
      }

      this.updateDialogState(false);
      this.trigger.setAttribute('aria-expanded', 'false');
      this.updateTriggerLabel(false);
      this.panel.setAttribute('aria-hidden', 'true');
      this.panel.inert = true;
      this.closeAllAccordions(true);

      const finish = () => this.finishClose(restoreFocus);
      if (immediate || this.reduceMotion) finish();
      else this.closeTimer = window.setTimeout(finish, MOTION_DURATION);
    }

    finishClose(restoreFocus) {
      if (this.state.drawerOpen) return;

      window.clearTimeout(this.closeTimer);
      this.dataset.state = 'closed';
      this.classList.remove('menu-open');
      this.updateDialogState(false);
      this.updateTriggerLabel(false);
      this.unlockPage();

      if (restoreFocus && this.isConnected) this.trigger?.focus({ preventScroll: true });

      this.dispatchEvent(
        new CustomEvent('ulif:mobile-drawer-close', { bubbles: true, detail: { drawer: this } })
      );
    }

    toggleAccordion(trigger) {
      const accordion = trigger.closest(ACCORDION_SELECTOR);
      if (!accordion || !this.contains(accordion)) return;

      const open = trigger.getAttribute('aria-expanded') !== 'true';
      if (open) this.closeAccordionSiblings(accordion);
      this.setAccordionOpen(accordion, open);
    }

    setAccordionOpen(accordion, open, immediate = false) {
      const trigger = accordion.querySelector(`:scope > ${ACCORDION_TRIGGER_SELECTOR}`);
      const panel = accordion.querySelector(`:scope > ${ACCORDION_PANEL_SELECTOR}`);
      if (!trigger || !panel) return;

      window.clearTimeout(accordion.__ulifCloseTimer);
      const key = accordion.dataset.key || null;
      const level = accordion.dataset.level;

      trigger.setAttribute('aria-expanded', String(open));
      panel.setAttribute('aria-hidden', String(!open));
      panel.inert = !open;

      if (open) {
        panel.hidden = false;
        panel.getBoundingClientRect();
        accordion.classList.add('is-expanded');
        if (level === 'section') this.state.openSection = key;
        if (level === 'group') this.state.openGroup = key;
        return;
      }

      accordion.classList.remove('is-expanded');
      if (level === 'section' && this.state.openSection === key) {
        this.state.openSection = null;
        accordion.querySelectorAll(`${ACCORDION_SELECTOR}.is-expanded`).forEach((child) => {
          this.setAccordionOpen(child, false, true);
        });
        this.state.openGroup = null;
      }
      if (level === 'group' && this.state.openGroup === key) this.state.openGroup = null;

      const finish = () => {
        if (trigger.getAttribute('aria-expanded') === 'false') panel.hidden = true;
      };

      if (immediate || this.reduceMotion) finish();
      else accordion.__ulifCloseTimer = window.setTimeout(finish, MOTION_DURATION);
    }

    closeAccordionSiblings(accordion) {
      const list = accordion.parentElement?.parentElement;
      if (!(list instanceof HTMLUListElement)) return;

      list.querySelectorAll(`:scope > li > ${ACCORDION_SELECTOR}.is-expanded`).forEach((sibling) => {
        if (sibling !== accordion) this.setAccordionOpen(sibling, false);
      });
    }

    closeAllAccordions(immediate = false) {
      this.querySelectorAll(`${ACCORDION_SELECTOR}.is-expanded`).forEach((accordion) => {
        this.setAccordionOpen(accordion, false, immediate);
      });
      this.state.openSection = null;
      this.state.openGroup = null;
    }

    updateTriggerLabel(open) {
      if (!this.trigger) return;
      const label = open ? this.trigger.dataset.labelClose : this.trigger.dataset.labelOpen;
      if (label) this.trigger.setAttribute('aria-label', label);
    }

    updateDialogState(open) {
      if (open) {
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-label', this.trigger?.dataset.labelOpen || 'Menu');
      } else {
        this.removeAttribute('role');
        this.removeAttribute('aria-modal');
        this.removeAttribute('aria-label');
      }
    }

    forceClosedState() {
      window.clearTimeout(this.closeTimer);
      window.clearTimeout(this.__ulifDrawerCloseTimer);
      this.state.drawerOpen = false;
      this.dataset.state = 'closed';
      this.classList.remove('menu-open');
      this.updateDialogState(false);
      this.trigger?.setAttribute('aria-expanded', 'false');
      this.updateTriggerLabel(false);
      this.panel?.setAttribute('aria-hidden', 'true');
      if (this.panel) this.panel.inert = true;
      this.closeAllAccordions(true);
      this.unlockPage();
    }

    observeSearchState() {
      this.searchStateObserver?.disconnect();
      this.searchStateObserver = new MutationObserver(() => {
        if (document.documentElement.classList.contains('ulif-search-is-open')) {
          this.handleSearchOpen();
        }
      });
      this.searchStateObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });

      if (document.documentElement.classList.contains('ulif-search-is-open')) {
        this.handleSearchOpen();
      }
    }

    lockPage() {
      if (this.scrollY === null) {
        const bootstrapScrollY = Number(this.dataset.ulifScrollY);
        this.scrollY = Number.isFinite(bootstrapScrollY) ? bootstrapScrollY : window.scrollY;
      }

      this.dataset.ulifScrollY = String(this.scrollY);
      document.documentElement.classList.add('ulif-mobile-menu-open');
    }

    unlockPage() {
      const scrollY = this.scrollY ?? Number(this.dataset.ulifScrollY);
      this.scrollY = null;
      delete this.dataset.ulifScrollY;

      if (!document.querySelector(`${ROOT_SELECTOR}[data-state='open'], ${ROOT_SELECTOR}[data-state='closing']`)) {
        document.documentElement.classList.remove('ulif-mobile-menu-open');
      }

      if (Number.isFinite(scrollY)) window.scrollTo(window.scrollX, scrollY);
    }

    trapTabKey(event) {
      const focusable = Array.from(
        this.querySelectorAll(
          'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])'
        )
      ).filter((element) => !element.closest('[hidden], [inert]') && element.getClientRects().length > 0);

      if (!focusable.length) {
        event.preventDefault();
        this.trigger?.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !this.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  if (!customElements.get('ulif-mobile-drawer')) {
    customElements.define('ulif-mobile-drawer', UlifMobileDrawer);
  }
})();
